import { ArrowForward } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Link } from 'react-router';

import { url } from '../../appRouter/url.ts';
import { AccordionWithHeading, TrpcLoadingInfo } from '../../components/general/index.js';
import { SelectableChipGroup } from '../../components/general/SelectableChipGroup.js';
import { GetDurchgaengeByAkaProduktIdsOutput, sapStatus, ThemaFromThemaRouter } from '../../dtos.js';
import { trpc } from '../../trpc.js';
import { formatDate, getExperteName } from '../../utils/index.js';
import { useOnlinePraesenzFilter } from './hooks/useOnlinePraesenzFilter.js';
import { usePaginatedList } from './hooks/usePaginatedList.js';
import { VeranstaltungOrt } from './VeranstaltungOrt.js';
import { VeranstaltungTrainer } from './VeranstaltungTrainer.js';

export const DurchgaengeList = ({
  thema,
  linkedThema,
}: {
  thema: ThemaFromThemaRouter;
  linkedThema?: ThemaFromThemaRouter;
}) => {
  const durchgaengeQuery = trpc.durchgang.getDurchgaengeByAkaProduktIds.useQuery({
    akaProduktIds: thema.verknuepfungBlopUndPraesenzseminar
      ? [thema.akaProduktId, thema.verknuepfungBlopUndPraesenzseminar]
      : [thema.akaProduktId],
  });
  const { data: durchgaenge } = durchgaengeQuery;
  const { displayedCount, showMore } = usePaginatedList();

  const { allOptions, selectedOptions, toggleOption } = useOnlinePraesenzFilter(thema, linkedThema);

  const filteredDurchgaenge = durchgaenge
    ? durchgaenge.filter((durchgang) => selectedOptions.includes(durchgang.akaProduktId))
    : [];

  return (
    <AccordionWithHeading heading="Durchgänge" bordered={false}>
      <TrpcLoadingInfo trpcQuery={durchgaengeQuery} entity="Durchgänge">
        {thema.verknuepfungBlopUndPraesenzseminar && (
          <SelectableChipGroup
            options={allOptions}
            selectedOptions={selectedOptions}
            onSelectionChange={toggleOption}
          />
        )}
        <Stack direction="column" gap={1.5} pt={1.5}>
          {filteredDurchgaenge.length === 0 && (
            <Typography variant="labelLight" color="textTertiary" textAlign="center" pt={1}>
              Wähle aus, ob du Präsenz- und/oder Online-Durchgänge sehen möchtest.
            </Typography>
          )}
          {filteredDurchgaenge.length > 0 &&
            filteredDurchgaenge
              .slice(0, displayedCount)
              .map((durchgang) => <DurchgangCard key={durchgang.id} durchgang={durchgang} />)}
          {filteredDurchgaenge.length > displayedCount && (
            <Button onClick={showMore} sx={{ color: 'text.primary' }}>
              Mehr anzeigen
            </Button>
          )}
        </Stack>
      </TrpcLoadingInfo>
    </AccordionWithHeading>
  );
};

const getVeranstaltungenDateRange = (durchgang: GetDurchgaengeByAkaProduktIdsOutput[number]) => (
  <Typography
    variant="labelLight"
    color={durchgang.sapStatus === sapStatus.ABGESAGT ? 'textTertiary' : undefined}
    sx={{ textDecoration: durchgang.sapStatus === sapStatus.ABGESAGT ? 'line-through' : undefined }}
  >
    {formatDate(durchgang.start) + ' - ' + formatDate(durchgang.end, true)}
  </Typography>
);

const getVeranstaltungenOrte = (veranstaltungen: GetDurchgaengeByAkaProduktIdsOutput[number]['modultermine']) => {
  // Ort:
  // - wenn Präsenz mit Online, dann nur Anzeige der Präsenz-Orte (location.kuerzel) (wenn es mehr als einen gibt, dann werden alle angezeigt)
  // - wenn Präsenz, dann Anzeige der Präsenz-Orte (location.kuerzel)(wenn es mehr als einen gibt, dann werden alle angezeigt)
  // - wenn Online, dann Anzeige des Meeting-Tools (planung.veranstaltung.online_tool)
  const isFullyOnline = veranstaltungen.every((va) => Boolean(va.onlineTool));

  const orte = new Set<string>();
  for (const veranstaltung of veranstaltungen) {
    if (isFullyOnline && veranstaltung.onlineTool) {
      orte.add(veranstaltung.onlineTool);
    } else {
      for (const blockung of veranstaltung.raumBlockungen) {
        orte.add(blockung.raum.location.kuerzel);
      }
    }
  }
  return Array.from(orte);
};

export const getVeranstaltungenTrainers = (
  veranstaltungen: GetDurchgaengeByAkaProduktIdsOutput[number]['modultermine'],
) => {
  const allTrainers = new Set<string>();
  for (const veranstaltung of veranstaltungen) {
    for (const blockung of veranstaltung.experteBlockungen) {
      allTrainers.add(getExperteName(blockung.experte));
    }
  }
  return Array.from(allTrainers);
};

const DurchgangCard = function ({ durchgang }: { durchgang: GetDurchgaengeByAkaProduktIdsOutput[number] }) {
  return (
    <Stack
      direction="row"
      sx={{
        backgroundColor: durchgang.sapStatus === sapStatus.ABGESAGT ? 'surface.background' : 'surface.brandLight',
        p: 1,
        borderRadius: '4px',
        border: (theme) =>
          durchgang.sapStatus === sapStatus.ABGESAGT ? `1px solid ${theme.palette.border.subtle}` : undefined,
      }}
      alignItems="center"
      justifyContent="space-between"
    >
      <Stack direction="column" spacing={0.5} flexGrow={1} width="calc(100% - 24px)">
        {getVeranstaltungenDateRange(durchgang)}
        <Box display="grid" gap={0.5} gridTemplateColumns="1fr 2fr">
          <VeranstaltungOrt
            online={durchgang.modultermine.every((va) => Boolean(va.onlineTool))}
            orte={getVeranstaltungenOrte(durchgang.modultermine)}
          />
          <VeranstaltungTrainer trainers={getVeranstaltungenTrainers(durchgang.modultermine)} />
        </Box>
      </Stack>
      <Link to={url.durchgang.details(durchgang.akaDurchgangId)} target="_blank">
        <ArrowForward />
      </Link>
    </Stack>
  );
};
